import styled from '@emotion/styled';
import { Button, HeadingText60, isHexColor, pxToRem } from '@modernhealth/vital-components';
import { useAnalytics } from '@modernhealth/web-analytics';
import { captureException } from '@sentry/browser';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import { navigationService } from 'src/config/rootService';
import { TopicOfFocusNode } from 'src/generated/webapp_gql';

import { BenefitItem } from '../components/BenefitContentCard';
import { BenefitModal } from '../components/BenefitModal';
import { BenefitPerCategoryPageTestId } from '../models/testsIds';

const Container = styled.div({
    paddingTop: '112px',
    display: 'flex',
    justifyContent: 'center',
});

const Content = styled.div({
    alignItems: 'center',
    display: 'flex',
    width: '512px',
    justifyContent: 'center',
    flexDirection: 'column',
});

const ImageContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '388px',
});

interface ImageBackgroundProp {
    color: string | undefined;
}
const ImageBackground = styled.div<ImageBackgroundProp>(({ color }) => ({
    backgroundColor: color ?? '',
    minWidth: '512px',
    minHeight: '200px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    borderRadius: '6px',
}));

const BenefitsContainer = styled.div({
    paddingTop: '20px',
    width: '512px',
});

const BenefitItemContainer = styled.div({
    paddingTop: '8px',
    paddingBottom: '8px',
});

const ButtonContainer = styled.div({
    paddingTop: '56px',
    paddingBottom: '84px',
    display: 'flex',
    width: '100%',
    minHeight: pxToRem(172),
});

const backToBenefits = () => {
    navigationService.navigate('/all-your-benefits');
};

interface Props {
    topicOfFocus?: TopicOfFocusNode;
}

export const BenefitPerCategoryPage: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation<Props | undefined>();
    const topicOfFocus = location?.state?.topicOfFocus;
    const [selectedBenefit, setSelectedBenefit] = useState(undefined);
    const [benefitModalVisible, setBenefitModalVisible] = useState(false);
    const analytics = useAnalytics();

    const closeBenefitModal = () => {
        setBenefitModalVisible(false);
    };

    const openBenefitModal = benefit => {
        setSelectedBenefit(benefit);
        setBenefitModalVisible(true);
        analytics.trackClick('Individual Benefit Selection', {
            button_copy: benefit.title,
        });
    };

    if (!topicOfFocus) {
        captureException(
            new Error(
                'User navigated to /benefit-per-category without a specified topic of focus.',
            ),
        );
        history.push('/all-your-benefits');
        return null;
    }

    return (
        <Container>
            <Content>
                <HeadingText60 testID={BenefitPerCategoryPageTestId.Header}>
                    {topicOfFocus.alias ? topicOfFocus.alias : topicOfFocus.name}
                </HeadingText60>
                <ImageBackground color={topicOfFocus.benefitColor || ''}>
                    <ImageContainer>
                        <img alt="" src={topicOfFocus.image} />
                    </ImageContainer>
                </ImageBackground>
                <BenefitsContainer>
                    {topicOfFocus.benefits.map(benefit => {
                        const element = {
                            ...benefit,
                            imageUrl: benefit.image || '',
                            color: isHexColor(benefit.color) ? benefit.color : undefined,
                        } as const;
                        return (
                            <BenefitItemContainer key={element.uuid}>
                                <BenefitItem element={element} onClick={openBenefitModal} />
                            </BenefitItemContainer>
                        );
                    })}
                </BenefitsContainer>
                <ButtonContainer>
                    <Button
                        icon="chevron-left"
                        variant="secondary"
                        data-testid={BenefitPerCategoryPageTestId.Button}
                        rounded
                        onClick={backToBenefits}
                    >
                        {t('benefits:benefit_per_category_page.return')}
                    </Button>
                </ButtonContainer>
                <BenefitModal
                    benefit={selectedBenefit}
                    visible={benefitModalVisible}
                    onClose={closeBenefitModal}
                />
            </Content>
        </Container>
    );
};
